// RestTimer — T-016 Part A. Lime ring draining counter-clockwise. In the
// final 3 seconds the ring turns --color-rust and pulses. Audio countdown
// fires from the parent so the timer is purely visual.

import { useEffect, useRef, useState } from "react";
import type { JSX } from "react";
import { useI18n } from "../../i18n";
import { useMediaQuery } from "../../hooks/useMediaQuery";

export interface RestTimerProps {
  /** Total rest duration in seconds. */
  seconds: number;
  /** Called exactly once when the countdown hits zero. */
  onComplete: () => void;
  /** Called once per integer-second change while secondsLeft is 3, 2, or 1. */
  onTick?: (secondsLeft: number) => void;
  /** Allow the user to skip the rest. */
  onSkip: () => void;
}

const RING_RADIUS = 56;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

export function RestTimer({ seconds, onComplete, onTick, onSkip }: RestTimerProps): JSX.Element {
  const { t } = useI18n();
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [remaining, setRemaining] = useState(seconds);
  const completedRef = useRef(false);
  const startMsRef = useRef<number>(Date.now());
  const startedAtMs = useRef<number>(seconds * 1000);
  const displayedRef = useRef(seconds);

  useEffect(() => {
    completedRef.current = false;
    setRemaining(seconds);
    startMsRef.current = Date.now();
    startedAtMs.current = seconds * 1000;
    displayedRef.current = seconds;
  }, [seconds]);

  useEffect(() => {
    const handle = window.setInterval(() => {
      const elapsed = Date.now() - startMsRef.current;
      const left = Math.max(0, startedAtMs.current - elapsed);
      const next = Math.ceil(left / 1000);
      if (next !== displayedRef.current) {
        displayedRef.current = next;
        setRemaining(next);
        if (next >= 1 && next <= 3) {
          onTick?.(next);
        }
      }
      if (left <= 0 && !completedRef.current) {
        completedRef.current = true;
        window.clearInterval(handle);
        onComplete();
      }
    }, 200);
    return () => window.clearInterval(handle);
  }, [onComplete, onTick]);

  const fraction = Math.max(0, Math.min(1, remaining / Math.max(1, seconds)));
  const dashOffset = RING_CIRCUMFERENCE * (1 - fraction);
  const inFinal3 = remaining > 0 && remaining <= 3;
  const ringColor = inFinal3 ? "var(--color-rust)" : "var(--color-lime)";

  return (
    <div
      data-testid="rest-timer"
      className="flex flex-col items-center gap-6"
      style={{ minHeight: "200px" }}
    >
      <p className="font-subhead text-sm uppercase tracking-[0.06em] text-[var(--color-ink)]">
        {t("rest.remaining", { seconds: remaining })}
      </p>
      <div
        data-pulse={inFinal3 && !reduceMotion}
        className={inFinal3 && !reduceMotion ? "animate-[pulse_1s_ease-in-out_infinite]" : ""}
        style={{ width: 144, height: 144 }}
      >
        <svg
          viewBox="0 0 144 144"
          width={144}
          height={144}
          role="img"
          aria-label={t("rest.remaining", { seconds: remaining })}
        >
          {/* Counter-clockwise: we draw the ring at the top and rotate the whole
              SVG -90deg so the start is at 12 o'clock. */}
          <g transform="rotate(-90 72 72)">
            <circle
              cx={72}
              cy={72}
              r={RING_RADIUS}
              fill="none"
              stroke="var(--color-line)"
              strokeWidth={4}
            />
            <circle
              cx={72}
              cy={72}
              r={RING_RADIUS}
              fill="none"
              stroke={ringColor}
              strokeWidth={4}
              strokeLinecap="round"
              strokeDasharray={RING_CIRCUMFERENCE}
              strokeDashoffset={dashOffset}
              style={{ transition: "stroke-dashoffset 200ms linear, stroke 120ms linear" }}
            />
          </g>
          <text
            x={72}
            y={72}
            textAnchor="middle"
            dominantBaseline="central"
            className="font-numeric"
            style={{
              fontSize: 44,
              fontWeight: 700,
              fill: "var(--color-ink-hi)",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {remaining}
          </text>
        </svg>
      </div>
      <button
        type="button"
        onClick={onSkip}
        data-testid="rest-skip"
        className="border-2 border-[var(--color-line-strong)] bg-[var(--color-slab)] px-6 font-subhead text-sm uppercase tracking-[0.06em] text-[var(--color-ink-hi)] hover:border-[var(--color-lime)] hover:bg-[var(--color-slab-2)] active:scale-95 transition-all shadow-md"
        style={{
          minHeight: "64px",
          borderRadius: "var(--radius-sharp)",
          minWidth: "200px",
        }}
      >
        {t("workoutRunner.actions.skip")}
      </button>
    </div>
  );
}
